import { memo } from 'react';
import { useSelector } from '@xstate/store/react';
import { ALL_CONNECTOR_SIDES } from '../../types';
import type { CanvasEntity } from '../../entityTypes';
import { getTextLabelData } from '../../entityTypes';
import { useCanvasStores } from '../../stores/CanvasContext';
import { useMergedDropTarget } from '../../hooks/useMergedDropTarget';
import { useLockedLayerIds } from '../../hooks/useLockedLayerIds';
import { useActiveMeasure } from '../../hooks/useActiveMeasure';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { useEntityData } from '../../stores/EntityDataContext';
import { CanvasConnector } from '../../components/CanvasConnector';
import { InlineRichEditor } from '../../components/InlineRichEditor';

export interface CanvasTextLabelProps {
  entity: CanvasEntity;
}

function CanvasTextLabelInner({ entity }: CanvasTextLabelProps) {
  const { store } = useCanvasStores();
  const { mergedDropTargetId, mergedDropTargetSide } = useMergedDropTarget();
  const { selectedEdgeConnectors, connectorHasEdge } = useEntityData();
  const lockedLayerIds = useLockedLayerIds();

  const d = getTextLabelData(entity);

  const isSelected = useSelector(canvasSelectionStore, s => s.context.selectedEntityIds.includes(entity.id));
  const isEditing = useSelector(canvasSelectionStore, s => s.context.editingElementId === entity.id);
  const isLocked = lockedLayerIds.has(entity.layerId);
  const isDropTarget = mergedDropTargetId === entity.id;
  const dropTargetSide = isDropTarget ? mergedDropTargetSide : null;

  const [containerRef, measuredSize] = useActiveMeasure(entity.id, isSelected || isEditing);

  return (
    <div
      ref={containerRef}
      data-canvas-entity
      data-canvas-textlabel={entity.id}
      style={{
        position: 'absolute',
        left: entity.x,
        top: entity.y,
        border: d.borderColor ? `${d.borderWidth ?? 1}px solid ${d.borderColor}` : undefined,
        borderRadius: d.borderColor ? 4 : undefined,
        padding: d.borderColor ? '2px 4px' : undefined,
      }}
      data-entity-id={entity.id}
      data-entity-kind={entity.kind}
      className={`select-none inline-block ${isSelected ? 'ring-2 ring-blue-400 rounded' : ''} ${isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
    >
      <style>{`
[data-canvas-textlabel] .tiptap-readonly p {
  margin: 0; padding: 0;
}
[data-canvas-textlabel] .tiptap-readonly p + p {
  margin-top: 2px;
}
      `}</style>
      {isEditing ? (
        <div
          style={{
            color: d.textColor,
            fontSize: d.fontSize,
            fontWeight: d.fontWeight,
          }}
        >
          <InlineRichEditor
            entityId={entity.id}
            html={d.html}
            text={d.text}
            onSave={(html, text) => {
              store.send({ type: 'textlabel.updateData', entityId: entity.id, dataChanges: { html, text } });
              canvasSelectionStore.send({ type: 'editing.stop' });
            }}
            onCancel={() => canvasSelectionStore.send({ type: 'editing.stop' })}
            className="whitespace-pre-wrap"
          />
        </div>
      ) : d.html ? (
        <span
          className="tiptap-readonly whitespace-pre-wrap"
          style={{
            color: d.textColor,
            fontSize: d.fontSize,
            fontWeight: d.fontWeight,
          }}
          dangerouslySetInnerHTML={{ __html: d.html }}
        />
      ) : (
        <span
          style={{
            color: d.textColor,
            fontSize: d.fontSize,
            fontWeight: d.fontWeight,
            whiteSpace: 'pre-wrap',
          }}
        >
          {d.text}
        </span>
      )}

      {/* Connectors */}
      {entity.connectable && ALL_CONNECTOR_SIDES.map(side => {
        const key = `${entity.id}:${side}`;
        return (
          <CanvasConnector
            key={side}
            taskId={entity.id}

            entityWidth={measuredSize.width}
            entityHeight={measuredSize.height}
            side={side}
            isDropTarget={isDropTarget}
            dropTargetSide={dropTargetSide}
            isEdgeEndpoint={selectedEdgeConnectors.has(key)}
            hasEdge={connectorHasEdge.has(key)}
          />
        );
      })}
    </div>
  );
}

export const CanvasTextLabel = memo(CanvasTextLabelInner);
